import { Profession } from '../types';
import { OverseasTerritoryId, findOverseasTerritoryById, OVERSEAS_TERRITORIES_DATA, OverseasTerritory } from '../types';
import { GradeData, EchelonData } from '../types';
import { 
  getGradeUrlSegment, 
  getEchelonUrlSegment, 
  parseGradeUrlSegment, 
  parseEchelonUrlSegment,
  findEchelonInGrade 
} from '../utils/urlUtils';

/**
 * @fileoverview Healthcare professions data with nursing specializations
 * 
 * This module contains all available healthcare professions and nursing specializations
 * supported by the salary simulator. Each profession has its own salary grid table
 * in Supabase and specific characteristics.
 * 
 * @author App'Ines Team
 * @version 2.0.0
 * @since 2025
 */

export const professions: Profession[] = [
  // Default IDE profession - no specific path (stays on main domain)
  {
    id: 'infirmier',
    name: 'Infirmier Diplômé d\'État (IDE)',
    category: 'Paramédical',
    // No path property - this profession stays on the main domain (/)
    supabaseTable: 'grille_Hopital_IDE',
    averageConsultationPrice: 30.6,
    averageActsPerWeek: 35,
    defaultRetrocessionPercentage: 0
  },
  
  // Nursing specializations with dedicated URLs
  {
    id: 'infirmiere-anesthesiste-iade',
    name: 'Infirmière Anesthésiste (IADE)',
    category: 'Paramédical Spécialisé',
    path: '/infirmiere-anesthesiste-iade',
    supabaseTable: 'grille_Hopital_IADE',
    averageConsultationPrice: 35.0,
    averageActsPerWeek: 35,
    defaultRetrocessionPercentage: 0
  },
  
  {
    id: 'infirmiere-bloc-operatoire-ibode',
    name: 'Infirmière de Bloc Opératoire (IBODE)',
    category: 'Paramédical Spécialisé',
    path: '/infirmiere-bloc-operatoire-ibode',
    supabaseTable: 'grille_Hopital_IBODE',
    averageConsultationPrice: 33.0,
    averageActsPerWeek: 35,
    defaultRetrocessionPercentage: 0
  },
  
  {
    id: 'infirmiere-puericultrice-ipde',
    name: 'Infirmière Puéricultrice (IPDE)',
    category: 'Paramédical Spécialisé',
    path: '/infirmiere-puericultrice-ipde',
    supabaseTable: 'grille_Hopital_IPDE',
    averageConsultationPrice: 32.0,
    averageActsPerWeek: 35,
    defaultRetrocessionPercentage: 0
  },
  
  {
    id: 'infirmiere-pratique-avancee-ipa',
    name: 'Infirmière en Pratique Avancée (IPA)',
    category: 'Paramédical Spécialisé',
    path: '/infirmiere-pratique-avancee-ipa',
    supabaseTable: 'grille_Hopital_IPA',
    averageConsultationPrice: 38.0,
    averageActsPerWeek: 35,
    defaultRetrocessionPercentage: 0
  }
];

/**
 * Helper function to find a profession by its ID
 * 
 * @param {string} professionId - The profession ID to search for
 * @returns {Profession | undefined} The found profession or undefined
 * 
 * @example
 * const iade = findProfessionById('infirmiere-anesthesiste-iade');
 * console.log(iade?.name); // 'Infirmière Anesthésiste (IADE)'
 */
export function findProfessionById(professionId: string): Profession | undefined {
  return professions.find(p => p.id === professionId);
}

/**
 * Helper function to find a profession by its URL path
 * 
 * @param {string} path - The URL path to search for
 * @returns {Profession | undefined} The found profession or undefined
 * 
 * @example
 * const iade = findProfessionByPath('/infirmiere-anesthesiste-iade');
 * console.log(iade?.name); // 'Infirmière Anesthésiste (IADE)'
 */
export function findProfessionByPath(path: string): Profession | undefined {
  return professions.find(p => p.path === path);
}

/**
 * Helper function to get the default profession (IDE)
 * 
 * @returns {Profession} The default IDE profession
 */
export function getDefaultProfession(): Profession {
  return professions[0]; // IDE is always the first profession
}

/**
 * Helper function to get all nursing specializations (excluding default IDE)
 * 
 * @returns {Profession[]} Array of nursing specializations
 */
export function getNursingSpecializations(): Profession[] {
  return professions.filter(p => p.path !== undefined);
}

/**
 * Helper function to get the URL path for a profession
 * 
 * @param {Profession} profession - The profession object
 * @returns {string} The URL path ('/' for default IDE, specific path for specializations)
 */
export function getProfessionPath(profession: Profession): string {
  console.log('🔍 getProfessionPath called with profession:', profession.id, 'path:', profession.path);
  const path = profession.path || '/';
  console.log('🔍 getProfessionPath returning:', path);
  return path;
}
/**
 * Helper function to get the complete simulator URL path for a profession and territory
 * 
 * @param {Profession} profession - The profession object
 * @param {OverseasTerritoryId} territoryId - The territory identifier (null for metropolitan France)
 * @param {number | null} gradeId - The grade identifier (optional)
 * @param {EchelonData | null} echelon - The echelon data (optional)
 * @returns {string} The complete URL path
 * 
 * @example
 * // Default IDE profession in metropolitan France
 * const path = getSimulatorPath(ideProf, null);
 * console.log(path); // '/'
 * 
 * @example
 * // Default IDE profession in Guadeloupe
 * const path = getSimulatorPath(ideProf, 'guadeloupe');
 * console.log(path); // '/guadeloupe'
 * 
 * @example
 * // IADE profession in Mayotte
 * const path = getSimulatorPath(iadeProf, 'mayotte');
 * console.log(path); // '/infirmiere-anesthesiste-iade/mayotte'
 * 
 * @example
 * // IADE profession in Mayotte with grade and echelon
 * const path = getSimulatorPath(iadeProf, 'mayotte', 1, echelonData);
 * console.log(path); // '/infirmiere-anesthesiste-iade/mayotte/grade-1/echelon-5'
 * 
 * @example
 * // Default IDE with temporary echelon
 * const path = getSimulatorPath(ideProf, null, 3, temporaryEchelon);
 * console.log(path); // '/grade-3/echelon-provisoire-1'
 */
export function getSimulatorPath(
  profession: Profession, 
  territoryId: OverseasTerritoryId,
  gradeId?: number | null,
  echelon?: EchelonData | null
): string {
  console.log('🔍 getSimulatorPath called with:', { 
    professionId: profession.id, 
    territoryId, 
    gradeId, 
    echelon: echelon ? { echelon: echelon.echelon, echelonLabel: echelon.echelonLabel } : null 
  });
  
  const territory = findOverseasTerritoryById(territoryId);
  
  // Build grade and echelon segments if provided
  let gradeEchelonSegments = '';
  if (gradeId !== null && gradeId !== undefined && echelon) {
    const gradeSegment = getGradeUrlSegment(gradeId);
    const echelonSegment = getEchelonUrlSegment(echelon);
    gradeEchelonSegments = `/${gradeSegment}/${echelonSegment}`;
    console.log('🔍 getSimulatorPath - Generated grade/echelon segments:', gradeEchelonSegments);
  }
  
  // Metropolitan France (territoryId = null)
  if (!territoryId || !territory) {
    const basePath = profession.path || '';
    const path = basePath + gradeEchelonSegments || '/';
    console.log('🔍 getSimulatorPath returning for metropolitan France:', path);
    return path;
  }
  
  // Default IDE profession with territory
  if (!profession.path) {
    const path = `/${territory.path}${gradeEchelonSegments}`;
    console.log('🔍 getSimulatorPath returning for default profession with territory:', path);
    return path;
  }
  
  // Specialized profession with territory
  const path = `${profession.path}/${territory.path}${gradeEchelonSegments}`;
  console.log('🔍 getSimulatorPath returning for specialized profession with territory:', path);
  return path;
}

/**
 * Helper function to parse URL parameters and extract profession and territory
 * 
 * @param {string} professionParam - The profession parameter from URL
 * @param {string} territoryParam - The territory parameter from URL
 * @param {string} gradeParam - The grade parameter from URL (optional)
 * @param {string} echelonParam - The echelon parameter from URL (optional)
 * @returns {object} Object containing resolved profession, territory, grade, and echelon
 * 
 * @example
 * // Parse /infirmiere-anesthesiste-iade/mayotte
 * const result = parseUrlParams('infirmiere-anesthesiste-iade', 'mayotte');
 * console.log(result.profession.id); // 'infirmiere-anesthesiste-iade'
 * console.log(result.territoryId); // 'mayotte'
 * 
 * @example
 * // Parse /infirmiere-anesthesiste-iade/mayotte/grade-1/echelon-5
 * const result = parseUrlParams('infirmiere-anesthesiste-iade', 'mayotte', 'grade-1', 'echelon-5');
 * console.log(result.gradeId); // 1
 * console.log(result.parsedEchelon); // { echelonNum: 5, echelonLabel: null }
 */
export function parseUrlParams(
  professionParam?: string, 
  territoryParam?: string,
  gradeParam?: string,
  echelonParam?: string
) {
  console.log('🔍 parseUrlParams called with:', { 
    professionParam, 
    territoryParam, 
    gradeParam, 
    echelonParam 
  });
  
  let profession: Profession;
  let territoryId: OverseasTerritoryId = null;
  let gradeId: number | null = null;
  let parsedEchelon: { echelonNum: number | null; echelonLabel: string | null } = { 
    echelonNum: null, 
    echelonLabel: null 
  };
  
  // Parse grade and echelon if provided
  if (gradeParam) {
    gradeId = parseGradeUrlSegment(gradeParam);
    console.log('🔍 parseUrlParams - Parsed gradeId:', gradeId);
  }
  
  if (echelonParam) {
    parsedEchelon = parseEchelonUrlSegment(echelonParam);
    console.log('🔍 parseUrlParams - Parsed echelon:', parsedEchelon);
  }
  
  // Case 1: No parameters - default IDE in metropolitan France
  if (!professionParam && !territoryParam) {
    profession = getDefaultProfession();
    territoryId = null;
    console.log('🔍 parseUrlParams - Case 1: No parameters');
  }
  // Case 2: Only one parameter - could be profession or territory
  else if (professionParam && !territoryParam) {
    // Try to find as profession first
    const foundProfession = findProfessionById(professionParam);
    if (foundProfession) {
      profession = foundProfession;
      territoryId = null;
      console.log('🔍 parseUrlParams - Case 2a: Found profession');
    } else {
      // Try to find as territory (default IDE profession)
      const territory = findOverseasTerritoryByPath(professionParam);
      if (territory) {
        profession = getDefaultProfession();
        territoryId = territory.id;
        console.log('🔍 parseUrlParams - Case 2b: Found territory');
      } else {
        // Invalid parameter - default to IDE in metropolitan France
        profession = getDefaultProfession();
        territoryId = null;
        console.log('🔍 parseUrlParams - Case 2c: Invalid parameter, using defaults');
      }
    }
  }
  // Case 3: Both parameters - profession and territory
  else if (professionParam && territoryParam) {
    const foundProfession = findProfessionById(professionParam);
    const territory = findOverseasTerritoryByPath(territoryParam);
    
    if (foundProfession && territory) {
      profession = foundProfession;
      territoryId = territory.id;
      console.log('🔍 parseUrlParams - Case 3a: Found both profession and territory');
    } else {
      // Invalid combination - default to IDE in metropolitan France
      profession = getDefaultProfession();
      territoryId = null;
      console.log('🔍 parseUrlParams - Case 3b: Invalid combination, using defaults');
    }
  }
  // Case 4: Only territory parameter (shouldn't happen with current routing)
  else {
    profession = getDefaultProfession();
    territoryId = null;
    console.log('🔍 parseUrlParams - Case 4: Fallback to defaults');
  }
  
  const result = { profession, territoryId, gradeId, parsedEchelon };
  console.log('🔍 parseUrlParams returning:', result);
  return result;
}

/**
 * Helper function to build complete URL path with all parameters
 * 
 * This function is a convenience wrapper around getSimulatorPath that handles
 * the common case of building URLs with all available parameters.
 * 
 * @param {Profession} profession - The profession object
 * @param {OverseasTerritoryId} territoryId - The territory identifier
 * @param {GradeData | null} grade - The grade data object (optional)
 * @param {EchelonData | null} echelon - The echelon data object (optional)
 * @returns {string} The complete URL path
 * 
 * @example
 * const path = buildCompleteSimulatorPath(profession, 'guyane', gradeData, echelonData);
 * console.log(path); // '/infirmiere-anesthesiste-iade/guyane/grade-1/echelon-5'
 */
export function buildCompleteSimulatorPath(
  profession: Profession,
  territoryId: OverseasTerritoryId,
  grade: GradeData | null = null,
  echelon: EchelonData | null = null
): string {
  const gradeId = grade?.id || null;
  return getSimulatorPath(profession, territoryId, gradeId, echelon);
}

/**
 * Helper function to extract grade and echelon from URL parameters
 * 
 * This function works with React Router's useParams to extract and validate
 * grade and echelon information from URL segments.
 * 
 * @param {Record<string, string | undefined>} params - URL parameters from useParams
 * @param {GradeData[]} grades - Available grades for validation
 * @returns {object} Object with validated grade and echelon data
 * 
 * @example
 * const params = { gradeId: 'grade-1', echelonId: 'echelon-5' };
 * const result = extractAndValidateGradeEchelon(params, grades);
 * console.log(result.grade); // GradeData object or null
 * console.log(result.echelon); // EchelonData object or null
 */
export function extractAndValidateGradeEchelon(
  params: Record<string, string | undefined>,
  grades: GradeData[]
): {
  grade: GradeData | null;
  echelon: EchelonData | null;
  isValid: boolean;
} {
  console.log('🔍 extractAndValidateGradeEchelon called with params:', params);
  console.log('🔍 extractAndValidateGradeEchelon grades available:', grades.length);
  
  // If no grade/echelon in URL, return null values (valid state)
  if (!params.gradeId || !params.echelonId) {
    console.log('🔍 extractAndValidateGradeEchelon - No grade/echelon in URL');
    return { grade: null, echelon: null, isValid: true };
  }
  
  // Parse URL segments
  const gradeId = parseGradeUrlSegment(params.gradeId);
  const parsedEchelon = parseEchelonUrlSegment(params.echelonId);
  
  console.log('🔍 extractAndValidateGradeEchelon - Parsed:', { gradeId, parsedEchelon });
  
  if (!gradeId) {
    console.log('🔍 extractAndValidateGradeEchelon - Invalid grade segment');
    return { grade: null, echelon: null, isValid: false };
  }
  
  // Find grade in available grades
  const grade = grades.find(g => g.id === gradeId);
  if (!grade) {
    console.log('🔍 extractAndValidateGradeEchelon - Grade not found in available grades');
    return { grade: null, echelon: null, isValid: false };
  }
  
  // Find echelon in grade
  const echelon = findEchelonInGrade(grade, parsedEchelon);
  if (!echelon) {
    console.log('🔍 extractAndValidateGradeEchelon - Echelon not found in grade');
    return { grade: null, echelon: null, isValid: false };
  }
  
  console.log('🔍 extractAndValidateGradeEchelon - Successfully found:', {
    grade: { id: grade.id, nom: grade.nom },
    echelon: { echelon: echelon.echelon, indiceMajoré: echelon.indiceMajoré }
  });
  
  return { grade, echelon, isValid: true };
}
/**
 * Helper function to find an overseas territory by its URL path
 * This function is used internally by parseUrlParams
 * 
 * @param {string} path - The URL path to search for
 * @returns {OverseasTerritory | undefined} The found territory or undefined
 */
function findOverseasTerritoryByPath(path: string): OverseasTerritory | undefined {
  return OVERSEAS_TERRITORIES_DATA.find(t => t.path === path);
}